import { KeyboardEvent, KeyboardEventHandler, SyntheticEvent, useEffect, useState } from "react";

// Interfaces
import { ITextField } from "./interfaces";

// Components
import TooltipText from "./TooltipText";

// MUI
import { Skeleton } from "@mui/material";
import TextFieldExtension from "./TextFieldExtension";

const isAllowedKey = (event: KeyboardEvent<HTMLDivElement>, allowDecimal = false) => {
	if (event.key === "Enter" || event.key === "Backspace") {
		return true;
	}

	const isNumber = /^\d$/i.test(event.key);
	const isDecimalIncluded = allowDecimal ? !/\./.test(event.key) : true;
	if (!isNumber && !/Arrow/.test(event.key) && isDecimalIncluded) {
		return false;
	}

	return true;
};

export default function SimpleTextField(props: Readonly<ITextField>) {
	const {
		text = "",
		modifier = "",
		skeletonWidth = 150,
		skeletonHeight = 30,
		sx = undefined,
		allowDecimal = false,
		editable = false,
		isLoading = false,
		onlyNumbers = false,
		openTooltip = false,
		outlined = false,
		// Callbacks
		isValid = () => true,
		handleKeyDownText = () => {},
		onActionUpdate = () => {},
		onChange = () => {},
		onFocus = () => {},
		onFocusOut = () => {},
	} = props;

	const [value, setValue] = useState("");
	const [border, setBorder] = useState(false);

	const handleChange = (event: SyntheticEvent) => {
		const target = event.target as HTMLInputElement;
		let localValue = target.value;
		if (typeof target.value !== "undefined") {
			switch (modifier) {
				case "toUpperCase":
					localValue = localValue.toUpperCase();
					break;
				case "toLowerCase":
					localValue = localValue.toLowerCase();
					break;
				default:
					break;
			}

			if (editable && isValid(localValue)) {
				setValue(localValue);
				onChange?.(localValue);
			}
		}
	};

	const handleClick = () => {
		editable && setBorder(true);
	};

	const handleFocusOut = () => {
		if (editable && !outlined) {
			setBorder(false);
		}

		if (text !== value) {
			onActionUpdate?.(value);
		}

		onFocusOut?.(value);
	};

	const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
		if (onlyNumbers && !isAllowedKey(event, allowDecimal)) {
			event.preventDefault();
			return;
		}

		if (event.key === "Enter") {
			event.preventDefault();
			handleKeyDownText?.(value);
		}
	};

	useEffect(() => {
		setValue(text);
		setBorder(outlined);

		return () => {
			setValue("");
			setBorder(false);
		};
	}, [text, outlined]);

	if (isLoading) {
		return <Skeleton variant="rectangular" width={skeletonWidth} height={skeletonHeight} sx={{ borderRadius: "6px", ...sx }} />;
	}

	if (openTooltip) {
		return (
			<TooltipText
				{...props}
				title={value}
				border={border}
				onChange={handleChange}
				onClick={handleClick}
				onBlur={handleFocusOut}
				onKeyDown={handleKeyDown}
				onFocus={() => onFocus?.()}
			/>
		);
	}

	return (
		<TextFieldExtension
			{...props}
			title={value}
			border={border}
			onChange={handleChange}
			onClick={handleClick}
			onBlur={handleFocusOut}
			onKeyDown={handleKeyDown}
			onFocus={() => onFocus?.()}
		/>
	);
}
